import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";
import { Field, Chip, RadioPill, FormSection, SuccessPanel } from "@/components/FormBits";
import { api, formatApiError } from "@/lib/api";

const TRANSITIONS = [
  "Domestic violence shelter transition", "Homeless shelter transition", "Homelessness",
  "Addiction recovery", "Transitional housing", "Poverty or financial hardship",
  "Recently obtained housing", "Other major life transition",
];

const FURNITURE_NEEDS = [
  "Couch / loveseat", "Living room chairs", "Dining table & chairs", "Bed frames",
  "Mattresses", "Dressers", "Nightstands", "Coffee / end tables", "Desk",
  "Bookshelves / storage", "Children's furniture", "Lamps",
];

const ESSENTIALS_NEEDS = [
  "Dishes & dinnerware", "Pots & pans", "Cooking utensils & silverware", "Cups & glasses",
  "Small appliances (microwave, toaster…)", "Bedding & sheets", "Blankets & comforters",
  "Towels & linens", "Pillows", "Shower curtains & caddies", "Laundry baskets", "Storage containers",
];

const initialForm = {
  full_name: "", phone: "", email: "", preferred_contact: "", city: "", zip_code: "",
  num_adults: "", num_children: "", children_ages: "", num_bedrooms: "", move_in_date: "", already_moved_in: "",
  transitions: [], furniture_needs: [], essentials_needs: [],
  can_pick_up: "", needs_delivery: "", stairs: "", elevator: "", delivery_instructions: "",
  referred_by_org: "", org_name: "", case_manager_name: "", referral_contact: "",
};

export default function ApplyToShop() {
  const [form, setForm] = useState(initialForm);
  const [ackNotFree, setAckNotFree] = useState(false);
  const [ackPricing, setAckPricing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleList = (key, val) =>
    setForm({ ...form, [key]: form[key].includes(val) ? form[key].filter((v) => v !== val) : [...form[key], val] });

  const submit = async (e) => {
    e.preventDefault();
    if (!ackNotFree || !ackPricing) {
      toast.error("Please check both required acknowledgments before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/applications", { ...form, ack_not_free: ackNotFree, ack_pricing: ackPricing });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-3xl px-4 pb-24 pt-40 sm:px-6">
        <SuccessPanel
          testid="application-success-message"
          title="APPLICATION RECEIVED — WELCOME TO YOUR FRESH START."
          message="We'll review your application and contact you about next steps and available inventory. Thank you for trusting us with this new beginning."
        />
      </div>
    );
  }

  const AckBox = ({ checked, onChange, children, testid }) => (
    <button type="button" data-testid={testid} onClick={() => onChange(!checked)} aria-pressed={checked}
      className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left text-sm leading-relaxed transition-colors ${
        checked ? "border-olive bg-beige text-olive" : "border-linen bg-cream text-moss hover:border-olive/50"
      }`}>
      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${checked ? "border-olive bg-olive text-cream" : "border-linen"}`}>
        {checked && "✓"}
      </span>
      <span>{children}</span>
    </button>
  );

  return (
    <div data-testid="apply-to-shop-page">
      <PageHero overline="Affordable Shopper Application" title="APPLY TO" italic="shop affordably." testid="apply-headline">
        A short, respectful application. We never require detailed information about trauma or abuse — just enough to
        understand your household's needs.
      </PageHero>

      <form onSubmit={submit} className="mx-auto max-w-4xl space-y-8 px-4 py-16 sm:px-6" data-testid="shopper-application-form">
        <FadeUp>
          <FormSection number="01" title="Personal Information">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Full name" required testid="app-name-field">
                <input data-testid="app-name-input" required className="input-field" value={form.full_name} onChange={set("full_name")} />
              </Field>
              <Field label="Phone number" required testid="app-phone-field">
                <input data-testid="app-phone-input" required type="tel" className="input-field" value={form.phone} onChange={set("phone")} />
              </Field>
              <Field label="Email" required testid="app-email-field">
                <input data-testid="app-email-input" required type="email" className="input-field" value={form.email} onChange={set("email")} />
              </Field>
              <Field label="Preferred contact method" testid="app-contact-method-field">
                <select data-testid="app-contact-method-select" className="input-field" value={form.preferred_contact} onChange={set("preferred_contact")}>
                  <option value="">Select…</option>
                  <option>Call</option><option>Text</option><option>Email</option>
                </select>
              </Field>
              <Field label="City" testid="app-city-field">
                <input data-testid="app-city-input" className="input-field" value={form.city} onChange={set("city")} />
              </Field>
              <Field label="ZIP code" testid="app-zip-field">
                <input data-testid="app-zip-input" className="input-field" maxLength={5} value={form.zip_code} onChange={set("zip_code")} />
              </Field>
            </div>
          </FormSection>
        </FadeUp>

        <FadeUp>
          <FormSection number="02" title="Household Information">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Number of adults" testid="app-adults-field">
                <input data-testid="app-adults-input" type="number" min="0" className="input-field" value={form.num_adults} onChange={set("num_adults")} />
              </Field>
              <Field label="Number of children" testid="app-children-field">
                <input data-testid="app-children-input" type="number" min="0" className="input-field" value={form.num_children} onChange={set("num_children")} />
              </Field>
              <Field label="Children's ages" testid="app-ages-field">
                <input data-testid="app-ages-input" className="input-field" placeholder="e.g. 3, 7, 12" value={form.children_ages} onChange={set("children_ages")} />
              </Field>
              <Field label="Number of bedrooms" testid="app-bedrooms-field">
                <input data-testid="app-bedrooms-input" type="number" min="0" className="input-field" value={form.num_bedrooms} onChange={set("num_bedrooms")} />
              </Field>
              <Field label="Move-in date" testid="app-movein-field">
                <input data-testid="app-movein-input" type="date" className="input-field" value={form.move_in_date} onChange={set("move_in_date")} />
              </Field>
              <div>
                <p className="mb-2 text-sm font-semibold text-olive">Have you already moved into your home?</p>
                <div className="flex gap-2">
                  {["Yes", "No"].map((v) => (
                    <RadioPill key={v} testid={`moved-in-${v.toLowerCase()}`} label={v} value={v} selected={form.already_moved_in} onSelect={(val) => setForm({ ...form, already_moved_in: val })} />
                  ))}
                </div>
              </div>
            </div>
          </FormSection>
        </FadeUp>

        <FadeUp>
          <FormSection number="03" title="Current Transition">
            <p className="text-sm text-moss">Select all that apply. No details needed — categories only.</p>
            <div className="flex flex-wrap gap-2">
              {TRANSITIONS.map((t) => (
                <Chip key={t} testid={`transition-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} label={t}
                  checked={form.transitions.includes(t)} onToggle={() => toggleList("transitions", t)} />
              ))}
            </div>
          </FormSection>
        </FadeUp>

        <FadeUp>
          <FormSection number="04" title="Furniture Needs">
            <div className="flex flex-wrap gap-2">
              {FURNITURE_NEEDS.map((t) => (
                <Chip key={t} testid={`furniture-need-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} label={t}
                  checked={form.furniture_needs.includes(t)} onToggle={() => toggleList("furniture_needs", t)} />
              ))}
            </div>
          </FormSection>
        </FadeUp>

        <FadeUp>
          <FormSection number="05" title="Household Essentials Needed">
            <div className="flex flex-wrap gap-2">
              {ESSENTIALS_NEEDS.map((t) => (
                <Chip key={t} testid={`essential-need-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} label={t}
                  checked={form.essentials_needs.includes(t)} onToggle={() => toggleList("essentials_needs", t)} />
              ))}
            </div>
          </FormSection>
        </FadeUp>

        <FadeUp>
          <FormSection number="06" title="Delivery or Pickup">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-sm font-semibold text-olive">Can you pick up items?</p>
                <div className="flex gap-2">
                  {["Yes", "No"].map((v) => (
                    <RadioPill key={v} testid={`can-pickup-${v.toLowerCase()}`} label={v} value={v} selected={form.can_pick_up} onSelect={(val) => setForm({ ...form, can_pick_up: val })} />
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-semibold text-olive">Do you need delivery?</p>
                <div className="flex gap-2">
                  {["Yes", "No"].map((v) => (
                    <RadioPill key={v} testid={`needs-delivery-${v.toLowerCase()}`} label={v} value={v} selected={form.needs_delivery} onSelect={(val) => setForm({ ...form, needs_delivery: val })} />
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-semibold text-olive">Are there stairs?</p>
                <div className="flex gap-2">
                  {["Yes", "No"].map((v) => (
                    <RadioPill key={v} testid={`app-stairs-${v.toLowerCase()}`} label={v} value={v} selected={form.stairs} onSelect={(val) => setForm({ ...form, stairs: val })} />
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-semibold text-olive">Is there an elevator?</p>
                <div className="flex gap-2">
                  {["Yes", "No", "N/A"].map((v) => (
                    <RadioPill key={v} testid={`app-elevator-${v.toLowerCase().replace("/", "")}`} label={v} value={v} selected={form.elevator} onSelect={(val) => setForm({ ...form, elevator: val })} />
                  ))}
                </div>
              </div>
            </div>
            <Field label="Special delivery instructions" testid="delivery-instructions-field">
              <textarea data-testid="delivery-instructions-input" rows={2} className="input-field" value={form.delivery_instructions} onChange={set("delivery_instructions")} />
            </Field>
          </FormSection>
        </FadeUp>

        <FadeUp>
          <FormSection number="07" title="Referral (Optional)">
            <div>
              <p className="mb-2 text-sm font-semibold text-olive">Were you referred by an organization?</p>
              <div className="flex gap-2">
                {["Yes", "No"].map((v) => (
                  <RadioPill key={v} testid={`referred-${v.toLowerCase()}`} label={v} value={v} selected={form.referred_by_org} onSelect={(val) => setForm({ ...form, referred_by_org: val })} />
                ))}
              </div>
            </div>
            {form.referred_by_org === "Yes" && (
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Organization name" testid="org-name-field">
                  <input data-testid="org-name-input" className="input-field" value={form.org_name} onChange={set("org_name")} />
                </Field>
                <Field label="Case manager or staff member name" testid="case-manager-field">
                  <input data-testid="case-manager-input" className="input-field" value={form.case_manager_name} onChange={set("case_manager_name")} />
                </Field>
                <Field label="Contact information" testid="referral-contact-field">
                  <input data-testid="referral-contact-input" className="input-field" value={form.referral_contact} onChange={set("referral_contact")} />
                </Field>
              </div>
            )}
          </FormSection>
        </FadeUp>

        <FadeUp>
          <FormSection number="08" title="Required Acknowledgments">
            <div className="space-y-3">
              <AckBox checked={ackNotFree} onChange={setAckNotFree} testid="ack-not-free-checkbox">
                I understand that Fresh Start Furnishings is a low-cost furniture program and <strong>not a free furniture giveaway</strong> —
                though generous community donations sometimes allow items to be provided at no cost, depending on
                available funding and household circumstances.
              </AckBox>
              <AckBox checked={ackPricing} onChange={setAckPricing} testid="ack-pricing-checkbox">
                I understand that individual items are priced separately, and whole-home furnishing assistance is subject
                to available inventory and program guidelines, with our goal of keeping the total cost at <strong>$500 or less</strong> —
                and occasionally, when donations allow, <strong>no cost at all</strong>.
              </AckBox>
            </div>
          </FormSection>
        </FadeUp>

        <FadeUp className="text-center">
          <button type="submit" data-testid="shopper-application-form-submit-button" disabled={submitting}
            className="btn-accent !px-12 !py-4 text-base disabled:opacity-60">
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {submitting ? "Submitting…" : "Submit My Application"}
          </button>
        </FadeUp>
      </form>
    </div>
  );
}
